export const themeScopeKey = (scope) => {
    switch (scope.kind) {
        case "iaf":
            return "iaf";
        case "brand":
            return `brand:${scope.brandId}`;
        case "product":
            return `product:${scope.productId}`;
        case "tenant":
            return `tenant:${scope.tenantId}:${scope.productId}`;
        case "role":
            return `role:${scope.tenantId}:${scope.productId}:${scope.roleId}`;
        case "user-accessibility":
            return `user-accessibility:${scope.tenantId}:${scope.userId}`;
        case "user":
            return `user:${scope.tenantId}:${scope.productId}:${scope.userId}`;
        case "screen":
            return `screen:${scope.tenantId}:${scope.productId}:${scope.userId}:${scope.screenId}`;
        case "preview":
            return `preview:${scope.tenantId}:${scope.productId}:${scope.userId}:${scope.screenId ?? "global"}:${scope.previewId}`;
    }
};
const conflict = (expectedEtag, current) => ({
    code: "etag-conflict",
    message: "The theme preference changed on another device or session.",
    ...(expectedEtag === undefined ? {} : { expectedEtag }),
    ...(current.etag === undefined ? {} : { actualEtag: current.etag }),
    currentPreference: current,
});
export class InMemoryThemePreferenceProvider {
    #preferences = new Map();
    #unavailable = false;
    constructor(seed = []) {
        for (const preference of seed) {
            this.#preferences.set(themeScopeKey(preference.scope), preference);
        }
    }
    setUnavailable(unavailable) {
        this.#unavailable = unavailable;
    }
    async read(request) {
        await Promise.resolve();
        if (this.#unavailable) {
            return {
                status: "unavailable",
                diagnostics: ["Mock provider is unavailable."],
            };
        }
        const preference = this.#preferences.get(themeScopeKey(request.scope));
        return preference
            ? { status: "found", preference, diagnostics: [] }
            : { status: "not-found", diagnostics: [] };
    }
    async write(request) {
        await Promise.resolve();
        if (this.#unavailable) {
            return {
                status: "unavailable",
                diagnostics: ["Mock provider is unavailable."],
            };
        }
        const key = themeScopeKey(request.preference.scope);
        const current = this.#preferences.get(key);
        if (current &&
            request.expectedEtag !== current.etag) {
            return {
                status: "conflict",
                conflict: conflict(request.expectedEtag, current),
                diagnostics: [],
            };
        }
        if (!current && request.expectedEtag !== undefined) {
            return {
                status: "conflict",
                conflict: {
                    code: "etag-conflict",
                    message: "The expected preference no longer exists.",
                    expectedEtag: request.expectedEtag,
                },
                diagnostics: [],
            };
        }
        const nextRevision = (current?.revision ?? 0) + 1;
        const saved = {
            ...request.preference,
            revision: nextRevision,
            etag: `W/"theme-${nextRevision}"`,
        };
        this.#preferences.set(key, saved);
        return { status: "saved", preference: saved, diagnostics: [] };
    }
    async delete(request) {
        await Promise.resolve();
        if (this.#unavailable) {
            return {
                status: "unavailable",
                diagnostics: ["Mock provider is unavailable."],
            };
        }
        const key = themeScopeKey(request.scope);
        const current = this.#preferences.get(key);
        if (!current)
            return { status: "not-found", diagnostics: [] };
        if (request.expectedEtag !== current.etag) {
            return {
                status: "conflict",
                conflict: conflict(request.expectedEtag, current),
                diagnostics: [],
            };
        }
        this.#preferences.delete(key);
        return { status: "deleted", diagnostics: [] };
    }
}
//# sourceMappingURL=provider.js.map